import { useEffect, useId, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Banner, Button, Checkbox, ErrorState, Loading, Modal, ModalBody, ModalFooter, ModalHeader, Textarea } from "@novemai/components";
import type { GeofencePoint } from "@novemai/time-attendance-sdk";
import { EnvelopeError, errorMessage } from "@/api/errors";
import { fetchGeofenceReview, recordGeofenceReview } from "./time-api";

const POINTS = ["clock_in", "clock_out"] as const;

interface Intent { readonly point: GeofencePoint; readonly reason: string; readonly key: string }

/** The dialog keeps an uncertain mutation's exact body/key until its receipt resolves. */
export function GeofenceReviewDialog({ entryId, mode, onClose, onPendingChange }: {
  readonly entryId: string;
  readonly mode: "worker_reason" | "supervisor_override" | "read";
  readonly onClose: () => void;
  readonly onPendingChange: (pending: boolean) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const client = useQueryClient();
  const fieldId = useId();
  const [point, setPoint] = useState<GeofencePoint | null>(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [pending, setPending] = useState<Intent | null>(null);
  const query = useQuery({ queryKey: ["time", "geofence-review", entryId], queryFn: () => fetchGeofenceReview(entryId) });
  useEffect(() => { onPendingChange(pending !== null); }, [onPendingChange, pending]);
  const mutation = useMutation({
    mutationFn: (intent: Intent) => recordGeofenceReview(entryId, mode === "supervisor_override" ? mode : "worker_reason", { point: intent.point, reason: intent.reason, confirmed: true }, intent.key),
    onSuccess: () => {
      setPending(null); setPoint(null); setReason(""); setConfirmed(false);
      void client.invalidateQueries({ queryKey: ["time"] });
    },
    onError: (error: unknown) => {
      if (error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500) {
        setPending(null);
        void client.invalidateQueries({ queryKey: ["time", "geofence-review", entryId] });
      }
    },
  });
  const frozen = pending !== null || mutation.isPending;
  const canClose = !frozen;
  const close = () => { if (canClose) onClose(); };
  function submit() {
    if (mutation.isPending || mode === "read") return;
    const intent = pending ?? (point && confirmed && reason.trim() && reason.trim().length <= 1000 ? { point, reason: reason.trim(), key: crypto.randomUUID() } : null);
    if (!intent) return;
    setPending(intent); mutation.mutate(intent);
  }
  return (
    <Modal open onClose={close} closeOnEscape={canClose} closeOnScrimClick={canClose}>
      <ModalHeader title={t("time.geofence.title")} />
      <ModalBody>
        <p className="mb-4 text-[var(--fg-secondary)]">{t("time.geofence.explanation")}</p>
        {query.isLoading ? <Loading label={t("time.loading")} rows={2} /> : query.isError ? <ErrorState title={t("time.error_title")} description={errorMessage(query.error, t)} /> : (
          <div className="space-y-4">
            {POINTS.map((name) => {
              const summary = query.data?.geofence_match?.[name];
              if (!summary) return null;
              const canWrite = mode === "worker_reason" ? summary.reason_required : mode === "supervisor_override" && summary.outcome === "out_of_bounds" && !summary.reason_required;
              return <section key={name} className="rounded-xl border border-[var(--border-default)] p-3">
                <h3 className="font-semibold">{t(`time.geofence.${name}`)}</h3>
                <p>{t(`time.geofence.outcome.${summary.outcome}`)}</p>
                {summary.reason_required ? <p className="text-[var(--fg-secondary)]">{t("time.geofence.reason_required")}</p> : null}
                {query.data?.reviews.filter((review) => review.point === name).map((review) => <div key={review.review_id} className="mt-2">
                  <p className="text-sm font-semibold">{t(`time.geofence.${review.kind}`)}</p>
                  <p className="whitespace-pre-wrap break-words">{review.reason_redacted ? t("time.geofence.redacted") : review.reason}</p>
                </div>)}
                {canWrite ? <Button variant="secondary" size="sm" disabled={frozen} onClick={() => { setPoint(name); setReason(""); setConfirmed(false); mutation.reset(); }}>
                  {t(mode === "worker_reason" ? "time.geofence.add_for_point" : "time.geofence.override_for_point", { point: t(`time.geofence.${name}`) })}
                </Button> : null}
              </section>;
            })}
            {point ? <div className="space-y-3">
              <label htmlFor={fieldId}>{t("time.geofence.reason_label", { point: t(`time.geofence.${point}`) })}</label>
              <Textarea id={fieldId} value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} disabled={frozen} />
              <label className="flex items-start gap-2"><Checkbox checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} disabled={frozen} />{t("time.geofence.confirm")}</label>
            </div> : null}
          </div>
        )}
        {mutation.isError ? <Banner severity="danger" title={t("time.geofence.error_title")}>{errorMessage(mutation.error, t)}</Banner> : null}
        {pending && !mutation.isPending ? <p role="status">{t("time.geofence.retry_hint")}</p> : null}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" disabled={!canClose} onClick={close}>{t("time.geofence.close")}</Button>
        {point ? <Button variant="primary" onClick={submit} disabled={mutation.isPending || (!pending && (!confirmed || !reason.trim() || reason.trim().length > 1000))}>{t(pending && !mutation.isPending ? "time.geofence.retry" : "time.geofence.save")}</Button> : null}
      </ModalFooter>
    </Modal>
  );
}
